import { isNapflixOSMode } from "./napflixOS";

// NapflixOS Route Manager v11
// Observational OS route/page state foundation. App.js remains source of UI truth.
let routeState = {
  currentPage: "home",
  previousPage: null,
  history: ["home"],
  lastChangedAt: Date.now(),
  returnTarget: null,
};

const listeners = new Set();

const notifyListeners = () => {
  listeners.forEach((listener) => listener(routeState));
};

export function getRouteState() {
  return routeState;
}

export function setRouteState(partial) {
  routeState = {
    ...routeState,
    ...partial,
    lastChangedAt: Date.now(),
  };

  notifyListeners();
  return routeState;
}

export function subscribeRouteState(listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function navigateToPage(page, options = {}) {
  if (!page || typeof window === "undefined") return getRouteState();

  const detail = options.detail ?? page;
  window.dispatchEvent(new CustomEvent("pj:setPage", { detail }));
  return getRouteState();
}

export function goBack(fallbackPage = "home") {
  const history = routeState.history;
  const previousPage = history.length > 1
    ? history[history.length - 2]
    : fallbackPage;

  return navigateToPage(routeState.returnTarget || previousPage || fallbackPage);
}

export function setReturnTarget(target) {
  return setRouteState({ returnTarget: target });
}

export function clearReturnTarget() {
  return setRouteState({ returnTarget: null });
}

const observePageChange = (page) => {
  const nextPage = page || "home";
  const previousPage = routeState.currentPage;
  const nextHistory =
    previousPage === nextPage
      ? routeState.history
      : [...routeState.history, nextPage].slice(-25);

  setRouteState({
    currentPage: nextPage,
    previousPage: previousPage === nextPage ? routeState.previousPage : previousPage,
    history: nextHistory,
  });
};

export function installRouteManager() {
  if (typeof window === "undefined" || !isNapflixOSMode()) {
    return () => {};
  }

  const onPageEvent = (event) => {
    observePageChange(event.detail || "home");
  };

  window.addEventListener("pj:setPage", onPageEvent);

  return () => {
    window.removeEventListener("pj:setPage", onPageEvent);
  };
}
